import { useCallback } from "react";
import { useNavigate, useTenantPathname } from "@/routing/accountRouting";
import { useLocation } from "react-router-dom";
import { Stack, Divider, NavLink, ActionIcon, Tooltip, Box } from "@mantine/core";
import { IconSettings, IconMenu2 } from "@tabler/icons-react";

type Props = {
  collapsed: boolean;
  children?: React.ReactNode;
  /** Section sub-navigation rendered at the top of the sidebar */
  sectionNav?: React.ReactNode;
  /** Sidebar type key for expand toggle (localStorage + event) */
  sidebarType?: string;
};

export default function SidebarShell({ collapsed, children, sectionNav, sidebarType }: Props) {
  // Raw pathname (with the `/a/<account>` prefix) is what settings-return-path
  // stores, since it is handed straight back to navigate() later; the tenant
  // pathname (prefix stripped) is what the route comparison below uses.
  const { pathname } = useLocation();
  const tenantPathname = useTenantPathname();
  const navigate = useNavigate();
  const isOnSettings = tenantPathname.startsWith("/settings");

  const handleExpand = useCallback(() => {
    if (!sidebarType) return;
    const key = `${sidebarType}-sidebar-collapsed`;
    localStorage.setItem(key, "false");
    window.dispatchEvent(new CustomEvent(`${sidebarType}-sidebar-toggle`));
  }, [sidebarType]);

  // When navigating to Settings, store current path so the back button can return here
  const handleSettingsClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      sessionStorage.setItem("settings-return-path", pathname);
      navigate("/settings/profile");
    },
    [pathname, navigate]
  );

  return (
    <Stack
      h="100%"
      gap={0}
      style={{ overflow: "hidden" }}
    >
      {/* Top: Expand button when collapsed */}
      {collapsed && sidebarType && (
        <Box py="sm" style={{ display: "flex", justifyContent: "center" }}>
          <ActionIcon
            variant="subtle"
            color="gray"
            size={36}
            radius="md"
            onClick={handleExpand}
            title="Expand sidebar"
          >
            <IconMenu2 size={18} stroke={1.5} />
          </ActionIcon>
        </Box>
      )}

      {/* Top: Section sub-navigation */}
      {sectionNav}

      {/* Middle: Context-specific content (scrollable) */}
      {children ? (
        <>
          <Divider />
          <Box style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            {children}
          </Box>
        </>
      ) : (
        <Box style={{ flex: 1 }} />
      )}

      {/* Bottom: Settings link (hidden when already on Settings) */}
      {!isOnSettings && (
        <>
          <Divider />
          {collapsed ? (
            <Stack gap="xs" align="center" py="md">
              <Tooltip label="Settings" position="right">
                <ActionIcon
                  variant="subtle"
                  color="blue"
                  size={40}
                  onClick={handleSettingsClick}
                >
                  <IconSettings size={20} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
            </Stack>
          ) : (
            <NavLink
              component="button"
              label="Settings"
              leftSection={<IconSettings size="1rem" stroke={1.5} />}
              color="blue"
              py="md"
              styles={{ root: { borderRadius: 0 } }}
              onClick={handleSettingsClick}
            />
          )}
        </>
      )}
    </Stack>
  );
}
